import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { analyzeMosaicRisk } from '../services/api';

const MosaicDetection = ({ embedded = false }) => {
  const [documents, setDocuments] = useState([
    { id: 'doc1', text: '', fileName: null }
  ]);
  const [result, setResult] = useState(null);
  const [inputMode, setInputMode] = useState('text'); // 'text' or 'file'
  const fileInputRefs = useRef({});

  const mosaicMutation = useMutation({
    mutationFn: (docs) => analyzeMosaicRisk(docs),
    onSuccess: (data) => {
      setResult(data);
    },
    onError: (error) => {
      console.error('Mosaic analysis failed:', error);
      setResult({ error: error.message });
    }
  });

  const addDocument = () => {
    const newId = `doc${documents.length + 1}`;
    setDocuments([...documents, { id: newId, text: '', fileName: null }]);
  };

  const removeDocument = (index) => {
    if (documents.length > 1) {
      setDocuments(documents.filter((_, i) => i !== index));
    }
  };

  const updateDocument = (index, text, fileName = null) => {
    const updated = [...documents];
    updated[index].text = text;
    updated[index].fileName = fileName;
    setDocuments(updated);
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;

    const fileName = file.name;
    const fileType = file.name.split('.').pop().toLowerCase();

    try {
      let text = '';

      if (fileType === 'txt') {
        text = await file.text();
      } else if (fileType === 'pdf') {
        // For PDF, we'll send to backend for extraction
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/v1/extract-text', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          text = data.text || '';
        } else {
          throw new Error('Failed to extract text from PDF');
        }
      } else {
        // For other file types, try reading as text
        text = await file.text();
      }

      updateDocument(index, text, fileName);
    } catch (error) {
      console.error('File upload failed:', error);
      alert(`Failed to read file: ${error.message}`);
    }
  };

  const handleAnalyze = () => {
    const validDocs = documents.filter(d => d.text.trim());
    if (validDocs.length < 2) {
      alert('Please enter at least 2 documents to analyze for mosaic effects');
      return;
    }
    mosaicMutation.mutate(validDocs);
  };

  const getRiskColor = (score) => {
    if (score >= 0.8) return 'var(--error)';
    if (score >= 0.6) return 'var(--warning)';
    return 'var(--success)';
  };

  const getClassificationStampClass = (level) => {
    const classes = {
      'TOP SECRET': 'stamp stamp-topsecret',
      'SECRET': 'stamp stamp-secret',
      'CONFIDENTIAL': 'stamp stamp-confidential',
      'UNCLASSIFIED': 'stamp stamp-unclassified'
    };
    return classes[level] || 'stamp stamp-confidential';
  };

  return (
    <div style={{ padding: embedded ? '0' : '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {!embedded && (
        <div style={{ marginBottom: '24px' }}>
          <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
            Mosaic Effect Detection
          </h1>
          <p className="mt-2 text-sm font-mono text-[var(--text-muted)]">
            Analyze multiple documents for mosaic effect risks - when individually unclassified
            information combines to reveal classified information.
          </p>
        </div>
      )}

      {/* Tip Banner */}
      <div className="vault-card p-3 mb-4" style={{ borderLeft: '3px solid var(--spotlight-gold)' }}>
        <p className="text-sm text-[var(--text-secondary)]">
          <span className="font-semibold text-[var(--spotlight-gold)]">Tip:</span> Upload 2+ documents that may be released together. The system will analyze
          if their combination reveals classified information that individual documents don't.
        </p>
      </div>

      {/* Input Mode Toggle */}
      <div className="mb-4">
        <div className="inline-flex rounded overflow-hidden" style={{ border: '1px solid var(--vault-border)' }}>
          <button
            onClick={() => setInputMode('text')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              inputMode === 'text'
                ? 'bg-[var(--spotlight-gold)] text-[var(--vault-bg)]'
                : 'bg-[var(--vault-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
            style={{ border: 'none', cursor: 'pointer' }}
          >
            Paste Text
          </button>
          <button
            onClick={() => setInputMode('file')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              inputMode === 'file'
                ? 'bg-[var(--spotlight-gold)] text-[var(--vault-bg)]'
                : 'bg-[var(--vault-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
            style={{ border: 'none', borderLeft: '1px solid var(--vault-border)', cursor: 'pointer' }}
          >
            Upload Files
          </button>
        </div>
      </div>

      {/* Document Input Section */}
      <div className="vault-card p-5 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">
            Documents to Analyze ({documents.length})
          </h2>
          <button
            onClick={addDocument}
            className="vault-btn vault-btn-secondary flex items-center gap-1.5"
            style={{ padding: '6px 14px', fontSize: '13px' }}
          >
            <span style={{ fontSize: '16px' }}>+</span> Add Document
          </button>
        </div>

        <div style={{
          display: 'grid',
          gap: '16px',
          gridTemplateColumns: documents.length > 2 ? 'repeat(2, 1fr)' : '1fr'
        }}>
          {documents.map((doc, index) => (
            <div key={doc.id} className="vault-card-elevated" style={{ padding: '16px' }}>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-[var(--text-primary)]" style={{ color: 'var(--text-primary) !important' }}>
                  Document {index + 1}
                  {doc.fileName && (
                    <span className="ml-2 text-xs font-normal text-[var(--text-muted)]">
                      ({doc.fileName})
                    </span>
                  )}
                </label>
                {documents.length > 1 && (
                  <button
                    onClick={() => removeDocument(index)}
                    style={{
                      backgroundColor: 'transparent',
                      color: 'var(--error)',
                      padding: '4px 8px',
                      borderRadius: '2px',
                      border: '1px solid var(--error)',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily: 'var(--font-display)'
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>

              {inputMode === 'file' ? (
                <div>
                  <input
                    type="file"
                    ref={(el) => fileInputRefs.current[index] = el}
                    accept=".pdf,.txt,.doc,.docx"
                    onChange={(e) => handleFileUpload(index, e.target.files[0])}
                    style={{ display: 'none' }}
                  />
                  <div
                    onClick={() => fileInputRefs.current[index]?.click()}
                    style={{
                      border: '2px dashed var(--vault-border)',
                      borderRadius: '4px',
                      padding: '24px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: doc.text ? 'rgba(74, 124, 89, 0.1)' : 'var(--vault-bg)',
                      transition: 'all 0.2s'
                    }}
                  >
                    {doc.text ? (
                      <div>
                        <div style={{ color: 'var(--success)', fontWeight: '500', marginBottom: '4px' }}>
                          File loaded successfully
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                          {doc.text.length.toLocaleString()} characters extracted
                        </div>
                        <div style={{ color: 'var(--spotlight-gold)', fontSize: '12px', marginTop: '8px' }}>
                          Click to replace
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                          Click to upload PDF, TXT, or DOCX
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '12px', opacity: 0.6 }}>
                          or drag and drop
                        </div>
                      </div>
                    )}
                  </div>
                  {doc.text && (
                    <details style={{ marginTop: '8px' }}>
                      <summary style={{ cursor: 'pointer', color: 'var(--spotlight-gold)', fontSize: '13px' }}>
                        Preview extracted text
                      </summary>
                      <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        backgroundColor: 'var(--vault-bg)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        maxHeight: '100px',
                        overflow: 'auto',
                        color: 'var(--text-secondary)',
                        border: '1px solid var(--vault-border)',
                        fontFamily: 'var(--font-mono)'
                      }}>
                        {doc.text.substring(0, 500)}...
                      </div>
                    </details>
                  )}
                </div>
              ) : (
                <textarea
                  value={doc.text}
                  onChange={(e) => updateDocument(index, e.target.value)}
                  placeholder="Paste document text here..."
                  className="document-input"
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    resize: 'vertical'
                  }}
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-4">
          <button
            onClick={handleAnalyze}
            disabled={mosaicMutation.isPending}
            className="vault-btn vault-btn-primary"
            style={{ fontSize: '14px' }}
          >
            {mosaicMutation.isPending ? (
              <>
                <span className="animate-spin inline-block">&#9203;</span> Analyzing...
              </>
            ) : (
              'Analyze for Mosaic Risks'
            )}
          </button>
          <span className="text-sm text-[var(--text-muted)]">
            {documents.filter(d => d.text.trim()).length} of {documents.length} documents have content
          </span>
        </div>
      </div>

      {/* Results Section */}
      {result && (
        <div className="vault-card" style={{
          padding: '24px',
          borderLeft: result.error
            ? '3px solid var(--error)'
            : (result.has_high_risk ? '3px solid var(--error)' : '3px solid var(--success)')
        }}>
          {result.error ? (
            <div>
              <h3 className="font-semibold mb-2 text-[var(--error)]">Analysis Error</h3>
              <p className="text-[var(--text-secondary)]">{result.error}</p>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-start mb-5">
                <div>
                  <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-3">
                    Analysis Results
                  </h2>
                  <div className="flex gap-3 items-center">
                    <span className={`stamp ${result.has_high_risk ? 'stamp-secret' : 'stamp-unclassified'}`}>
                      {result.has_high_risk ? 'HIGH RISK' : 'LOW RISK'}
                    </span>
                    <span style={{
                      color: result.has_high_risk ? 'var(--error)' : 'var(--success)',
                      fontWeight: '500',
                      fontSize: '14px'
                    }}>
                      {result.risk_count} mosaic risk{result.risk_count !== 1 ? 's' : ''} detected
                    </span>
                  </div>
                </div>
              </div>

              {/* Recommendation */}
              <div className="vault-card-elevated mb-5" style={{
                padding: '16px',
                borderLeft: `3px solid ${result.has_high_risk ? 'var(--error)' : 'var(--success)'}`
              }}>
                <h3 className="font-semibold mb-2 text-sm uppercase tracking-wider text-[var(--text-primary)]">
                  Recommendation
                </h3>
                <p className="text-sm text-[var(--text-secondary)]" style={{ lineHeight: '1.6' }}>
                  {result.recommendation}
                </p>
              </div>

              {/* Individual Risks */}
              {result.risks && result.risks.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-4 text-base text-[var(--text-primary)]">
                    Detected Risks
                  </h3>
                  <div className="grid gap-4">
                    {result.risks.map((risk, index) => (
                      <div
                        key={risk.risk_id}
                        className="vault-card-elevated"
                        style={{
                          padding: '20px',
                          borderLeft: `3px solid ${getRiskColor(risk.risk_score)}`
                        }}
                      >
                        <div className="flex justify-between items-center mb-3">
                          <span className="font-semibold text-[var(--text-primary)]">Risk #{index + 1}</span>
                          <span style={{
                            backgroundColor: getRiskColor(risk.risk_score),
                            color: 'var(--vault-bg)',
                            padding: '4px 12px',
                            borderRadius: '2px',
                            fontSize: '12px',
                            fontWeight: '600',
                            fontFamily: 'var(--font-mono)'
                          }}>
                            {(risk.risk_score * 100).toFixed(0)}% confidence
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <span className="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">Triggered Rule</span>
                            <div className="mt-1">
                              <Link
                                to={`/scg?search=${encodeURIComponent(risk.triggered_rule_id)}`}
                                style={{
                                  color: 'var(--spotlight-gold)',
                                  fontWeight: '500',
                                  textDecoration: 'none',
                                  fontSize: '14px'
                                }}
                              >
                                {risk.triggered_rule_id} &rarr;
                              </Link>
                            </div>
                          </div>
                          <div>
                            <span className="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">Resulting Classification</span>
                            <div className="mt-1">
                              <span className={getClassificationStampClass(risk.resulting_classification)} style={{ fontSize: '11px', padding: '3px 10px' }}>
                                {risk.resulting_classification}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="mb-3">
                          <span className="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">Explanation</span>
                          <p className="mt-1 text-sm text-[var(--text-secondary)]" style={{ lineHeight: '1.5' }}>
                            {risk.explanation}
                          </p>
                        </div>

                        <div className="mb-3">
                          <span className="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">Mitigation</span>
                          <p className="mt-1 text-sm text-[var(--text-secondary)]" style={{ lineHeight: '1.5' }}>
                            {risk.recommendation}
                          </p>
                        </div>

                        {/* Elements involved */}
                        <details style={{ marginTop: '8px' }}>
                          <summary style={{
                            cursor: 'pointer',
                            color: 'var(--spotlight-gold)',
                            fontWeight: '500',
                            fontSize: '13px'
                          }}>
                            View {risk.elements.length} contributing elements
                          </summary>
                          <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            backgroundColor: 'var(--vault-bg)',
                            borderRadius: '4px',
                            border: '1px solid var(--vault-border)',
                            display: 'grid',
                            gap: '8px'
                          }}>
                            {risk.elements.map((elem) => (
                              <div key={elem.id} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '8px',
                                fontSize: '13px',
                                padding: '6px 8px',
                                backgroundColor: 'var(--vault-elevated)',
                                borderRadius: '2px',
                                border: '1px solid var(--vault-border)'
                              }}>
                                <span style={{
                                  backgroundColor: 'var(--vault-border)',
                                  color: 'var(--text-secondary)',
                                  padding: '2px 8px',
                                  borderRadius: '2px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap',
                                  fontFamily: 'var(--font-mono)',
                                  textTransform: 'uppercase'
                                }}>
                                  {elem.element_type}
                                </span>
                                <div>
                                  <strong style={{ color: 'var(--text-primary)' }}>{elem.value}</strong>
                                  <span style={{ color: 'var(--text-muted)' }}> — {elem.context}</span>
                                  <span style={{
                                    color: 'var(--text-muted)',
                                    marginLeft: '8px',
                                    fontSize: '11px',
                                    fontFamily: 'var(--font-mono)'
                                  }}>
                                    ({elem.source_doc_id})
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MosaicDetection;
